import * as React from "react";
import { ArrowLeft, LockKeyhole, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const DEMO_ACCESS_PASSWORD = "241224";

interface DemoAccessGateProps {
  children: React.ReactNode;
}

export function DemoAccessGate({ children }: DemoAccessGateProps) {
  const navigate = useNavigate();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [password, setPassword] = React.useState("");
  const [accessGranted, setAccessGranted] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!accessGranted) inputRef.current?.focus();
  }, [accessGranted]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (password.trim() === DEMO_ACCESS_PASSWORD) {
      setAccessGranted(true);
      setPassword("");
      setError("");
      return;
    }

    setPassword("");
    setError("Senha incorreta.");
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  if (accessGranted) return <>{children}</>;

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#061017] px-4 py-8 text-white">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm rounded-lg border border-white/12 bg-white/[0.055] p-5 shadow-[0_24px_70px_-36px_rgba(45,212,191,0.65)] backdrop-blur-xl sm:p-6"
      >
        <div className="mb-6 flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-md border border-cyan-200/24 bg-cyan-300/12 text-cyan-100">
            <LockKeyhole className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-100/70">
              AQL Vision
            </p>
            <h1 className="text-xl font-semibold text-white">Acesso demo</h1>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="demo-access-password" className="text-white/82">
            Senha de acesso
          </Label>
          <Input
            ref={inputRef}
            id="demo-access-password"
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              if (error) setError("");
            }}
            className="border-white/12 bg-black/26 text-white placeholder:text-white/35"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "demo-access-error" : undefined}
            autoComplete="off"
          />
          <p
            id="demo-access-error"
            role="status"
            aria-live="polite"
            className="min-h-5 text-sm text-rose-200"
          >
            {error}
          </p>
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-white/68 hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <Button type="submit" className="bg-cyan-200 text-slate-950 hover:bg-cyan-100">
            <ShieldCheck className="h-4 w-4" />
            Entrar
          </Button>
        </div>
      </form>
    </main>
  );
}
